import { Currency } from '@nemuru-eng/js-api-client';
import { useState } from 'react';

import { AutomobileFormItem } from '@/components/modules/Application/components/Cart/DefaultCart/components/ArticleTypes/Automobile/types/AutomobileFormItem';
import { useEditAutoPaymentMethodCapabilities } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/components/EditAutoPaymentMethodDialog/hooks/useEditAutoPaymentMethodCapabilities';
import { useEditAutoPaymentMethodConfirmation } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/components/EditAutoPaymentMethodDialog/hooks/useEditAutoPaymentMethodConfirmation';
import { useEditAutoPaymentMethodMutation } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/components/EditAutoPaymentMethodDialog/hooks/useEditAutoPaymentMethodMutation';
import { EditAutoPaymentMethodDialogStep } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/components/EditAutoPaymentMethodDialog/types/EditAutoPaymentMethodDialogStep';
import { EditAutoPaymentMethodEntryMode } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/components/EditAutoPaymentMethodDialog/types/EditAutoPaymentMethodEntryMode';
import { FinancingLiveSummaryData } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/components/EditAutoPaymentMethodDialog/types/FinancingLiveSummaryData';

interface UseEditAutoPaymentMethodFlowParams {
  entryMode: EditAutoPaymentMethodEntryMode;
  step: EditAutoPaymentMethodDialogStep;
  checkoutSessionId: string;
  currency: Currency;
  initialFormItem: AutomobileFormItem;
  initialFinancing: FinancingLiveSummaryData;
  onClose: (_, reason: string | undefined) => void;
  onNext: () => void;
  goToStep: (step: EditAutoPaymentMethodDialogStep) => void;
  refreshCheckoutSessionAndPaymentMethodEntities: () => Promise<void>;
}

export const useEditAutoPaymentMethodFlow = ({
  entryMode,
  step,
  checkoutSessionId,
  currency,
  initialFormItem,
  initialFinancing,
  onClose,
  onNext,
  goToStep,
  refreshCheckoutSessionAndPaymentMethodEntities,
}: UseEditAutoPaymentMethodFlowParams) => {
  const {
    editableFields,
    isSimulatorEnabled,
    showVehicleAmountControl,
    currentPaymentMethodOptions,
    paymentMethodId,
    fallbackApr,
    lenderName,
    lenderRequestId,
    isFieldDisabled,
  } = useEditAutoPaymentMethodCapabilities();

  const {
    lastConfirmedVehicleAmount,
    lastConfirmedRegistrationDate,
    selectedPaymentMethodOptions,
    needsFinancingReselection,
    requiresSimulatorFromVehicle,
    isFormDirty,
    hasErrors,
    onFinancingConditionsSelected,
    clearSelectedPaymentMethodOptions,
    resetSimulatorSelectionToInitial,
  } = useEditAutoPaymentMethodConfirmation({
    entryMode,
    initialFormItem,
    initialFinancing,
  });

  const {
    reviewItem,
    reviewFinancing,
    modificationBody,
    previewError,
    loadingPreview,
    paymentMethodModificationPreview,
    loadingApply,
    showRequestDelayWarning,
    setShowRequestDelayWarning,
    clearDelayWarningTimeout,
    resetApply,
    onPreview,
    onApply,
  } = useEditAutoPaymentMethodMutation({
    checkoutSessionId,
    currency,
    initialFormItem,
    initialFinancing,
    paymentMethodId,
    currentPaymentMethodOptions,
    selectedPaymentMethodOptions,
    goToStep,
    onNext,
    refreshCheckoutSessionAndPaymentMethodEntities,
  });

  const [showLiveSummary, setShowLiveSummary] = useState(step === EditAutoPaymentMethodDialogStep.VEHICLE);

  const isNextDisabled =
    step === EditAutoPaymentMethodDialogStep.VEHICLE
      ? !isFormDirty || hasErrors
      : step === EditAutoPaymentMethodDialogStep.SIMULATOR
      ? !isFormDirty || needsFinancingReselection
      : false;

  const onVehicleNext = () => {
    if (requiresSimulatorFromVehicle) {
      goToStep(EditAutoPaymentMethodDialogStep.SIMULATOR);
      return;
    }

    void onPreview();
  };

  const onReviewPrev = () => {
    resetApply();

    if (entryMode === 'financing' || requiresSimulatorFromVehicle) {
      goToStep(EditAutoPaymentMethodDialogStep.SIMULATOR);
      return;
    }

    goToStep(EditAutoPaymentMethodDialogStep.VEHICLE);
  };

  const onSimulatorPrev = () => {
    if (entryMode === 'vehicle') {
      goToStep(EditAutoPaymentMethodDialogStep.VEHICLE);
      return;
    }

    onClose(null, undefined);
  };

  return {
    capabilities: {
      editableFields,
      isSimulatorEnabled,
      showVehicleAmountControl,
      paymentMethodId,
      fallbackApr,
      lenderName,
      lenderRequestId,
      isFieldDisabled,
    },
    confirmation: {
      lastConfirmedVehicleAmount,
      lastConfirmedRegistrationDate,
      selectedPaymentMethodOptions,
      needsFinancingReselection,
      requiresSimulatorFromVehicle,
      hasErrors,
      onFinancingConditionsSelected,
      clearSelectedPaymentMethodOptions,
      resetSimulatorSelectionToInitial,
    },
    mutation: {
      reviewItem,
      reviewFinancing,
      modificationBody,
      previewError,
      loadingPreview,
      paymentMethodModificationPreview,
      loadingApply,
      showRequestDelayWarning,
      setShowRequestDelayWarning,
      clearDelayWarningTimeout,
      onPreview,
      onApply,
    },
    navigation: {
      isNextDisabled,
      onVehicleNext,
      onReviewPrev,
      onSimulatorPrev,
    },
    liveSummary: {
      showLiveSummary,
      setShowLiveSummary,
    },
  };
};
